import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, BookOpen, Clock, Users, Check, Sparkles, MountainSnow } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_KEY = "goldenelia_recipe_email";

const CATEGORY_LABELS = {
  starter: "Ορεκτικό",
  main: "Κυρίως",
  side: "Συνοδευτικό",
  dessert: "Επιδόρπιο",
  breakfast: "Πρωινό",
};

const SEASON_LABELS = {
  summer: "🌞 Καλοκαίρι",
  winter: "❄️ Χειμώνας",
  spring: "🌸 Άνοιξη",
  all: "Όλον τον χρόνο",
};

export const RecipeBook = ({ open, onClose }) => {
  const [preview, setPreview] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [unlocked, setUnlocked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [openRecipe, setOpenRecipe] = useState(null);
  const [requestForm, setRequestForm] = useState({ email: "", full_name: "", phone: "", plan: "one_time" });

  // Load preview + auto-unlock if admin OR auto-check if email saved
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/recipes/preview`).then(({ data }) => setPreview(data)).catch(() => {});

    // 1) Admin auto-unlock — bypass paywall completely
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(({ data: me }) => {
      if (me?.role === "admin") {
        axios.get(`${API}/admin/recipes`, { withCredentials: true }).then(({ data }) => {
          setUnlocked({
            unlocked: true,
            source: "admin",
            recipes: data.recipes || [],
            rotation: { is_rotating: false, library_size: data.total },
          });
        }).catch(() => {});
        return;
      }
      // 2) Not admin — fall back to saved email check
      const savedEmail = localStorage.getItem(ACCESS_KEY);
      if (savedEmail && !unlocked) {
        setEmailInput(savedEmail);
        checkAccess(savedEmail, false);
      }
    }).catch(() => {
      // Not authenticated — fall back to saved email check
      const savedEmail = localStorage.getItem(ACCESS_KEY);
      if (savedEmail && !unlocked) {
        setEmailInput(savedEmail);
        checkAccess(savedEmail, false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock body scroll while preserving scroll position (Radix Dialog also locks but doesn't preserve)
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    return () => {
      // Restore scroll after Radix releases body lock
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    };
  }, [open]);

  const checkAccess = async (email, showToast = true) => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/recipes/check-access`, { email });
      if (data.unlocked) {
        setUnlocked(data);
        localStorage.setItem(ACCESS_KEY, email.toLowerCase());
        if (showToast) toast.success(`Καλώς ήρθες! ${data.recipes.length} συνταγές ξεκλειδώθηκαν.`);
      } else {
        setUnlocked(null);
        if (showToast) toast.error("Αυτό το email δεν έχει πρόσβαση. Κάνε αίτημα παρακάτω.");
      }
    } catch {
      if (showToast) toast.error("Σφάλμα ελέγχου. Δοκίμασε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.email || !requestForm.full_name) return;
    try {
      await axios.post(`${API}/recipes/request-access`, requestForm);
      toast.success("Το αίτημά σου στάλθηκε! Θα επικοινωνήσουμε εντός 24h για την πληρωμή.");
      setShowRequest(false);
      setRequestForm({ email: "", full_name: "", phone: "", plan: "one_time" });
    } catch {
      toast.error("Σφάλμα. Στείλε email στο admin@goldenelia.gr");
    }
  };

  const logout = () => {
    localStorage.removeItem(ACCESS_KEY);
    setUnlocked(null);
    setEmailInput("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="recipe-book-dialog"
        className="!max-w-5xl w-[96vw] sm:w-[92vw] max-h-[88vh] overflow-y-auto p-0 bg-black border border-[color:var(--goldenelia-gold)]/45 recipe-modal-scroll"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <DialogTitle className="sr-only">Το Βιβλίο των Συνταγών</DialogTitle>
        {unlocked ? (
          <UnlockedView
            data={unlocked}
            onLogout={logout}
            onPickRecipe={setOpenRecipe}
          />
        ) : preview ? (
          <LockedView
            preview={preview}
            emailInput={emailInput}
            setEmailInput={setEmailInput}
            onCheck={() => checkAccess(emailInput)}
            loading={loading}
            onRequest={(plan) => { setRequestForm((f) => ({ ...f, plan })); setShowRequest(true); }}
          />
        ) : (
          <div className="p-16 text-center">
            <p className="font-display text-[10px] tracking-[0.5em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
              <Lock size={12} className="inline -translate-y-0.5 mr-1" /> Premium Συλλογή
            </p>
            <h2 className="font-serif-luxury text-3xl sm:text-4xl gold-gradient-text mb-6 leading-tight">
              Το Βιβλίο των Συνταγών
            </h2>
            <div className="gold-divider max-w-xs mx-auto mb-6"><span className="diamond" /></div>
            <div className="inline-flex items-center gap-3 font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-cream)]/65">
              <span className="w-2 h-2 rounded-full bg-[color:var(--goldenelia-gold)] animate-pulse" />
              Φόρτωση συλλογής...
            </div>
          </div>
        )}

        {showRequest && (
          <RequestModal
            form={requestForm}
            setForm={setRequestForm}
            onSubmit={submitRequest}
            onClose={() => setShowRequest(false)}
            price={preview?.price}
            subPrice={preview?.subscription_price}
          />
        )}
        {openRecipe && <RecipeModal recipe={openRecipe} onClose={() => setOpenRecipe(null)} />}
      </DialogContent>
    </Dialog>
  );
};

/* ============ LOCKED VIEW (Pricing/Email gate) ============ */
const LockedView = ({ preview, emailInput, setEmailInput, onCheck, loading, onRequest }) => (
  <div className="p-7 sm:p-12">
    <div className="text-center mb-10">
      <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
        <Lock size={12} className="inline -translate-y-0.5 mr-1" /> Premium Συλλογή
      </p>
      <h2 className="font-serif-luxury text-3xl sm:text-5xl gold-gradient-text mb-4 leading-tight">
        Το Βιβλίο των Συνταγών
      </h2>
      <div className="gold-divider max-w-xs mx-auto mb-6"><span className="diamond" /></div>
      <p className="font-serif-luxury italic text-base sm:text-lg text-[color:var(--goldenelia-cream)]/85 max-w-2xl mx-auto leading-relaxed">
        {preview.total_recipes}+ αυθεντικές κρητικές συνταγές με προϊόντα Golden Elia.
      </p>
      <p className="font-body text-sm text-[color:var(--goldenelia-gold)]/85 mt-4 max-w-xl mx-auto leading-relaxed inline-flex items-start gap-2 justify-center">
        <MountainSnow size={16} className="mt-0.5 shrink-0" />
        <span><em>Οι συνταγές έρχονται από χωριά της Κρήτης που δύσκολα θα βρεις — οικογενειακά μυστικά 4 γενεών που μεταφέρονται από γιαγιά σε εγγονή.</em></span>
      </p>
    </div>

    {/* Preview teaser */}
    <div className="grid sm:grid-cols-2 gap-3 mb-10 max-w-3xl mx-auto">
      {preview.preview.map((r) => (
        <div key={r.slug} className="p-4 border border-[color:var(--goldenelia-gold)]/25 bg-black/40">
          <p className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
            {CATEGORY_LABELS[r.category]}
          </p>
          <h3 className="font-serif-luxury text-base text-[color:var(--goldenelia-cream)] mb-1">{r.title}</h3>
          <p className="font-body text-[11px] text-[color:var(--goldenelia-cream)]/55">⏱ {r.time} · 👥 {r.serves} άτομα</p>
        </div>
      ))}
    </div>

    {/* Already have access? */}
    <div className="max-w-md mx-auto mb-10 p-5 border border-[color:var(--goldenelia-gold)]/30 bg-black/40">
      <p className="font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)] mb-3 text-center">
        Έχεις ήδη πρόσβαση;
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          placeholder="Email σου"
          data-testid="recipe-access-email"
          className="flex-1 bg-black border border-[color:var(--goldenelia-gold)]/40 px-3 py-2 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none"
        />
        <button
          onClick={onCheck}
          disabled={loading || !emailInput}
          data-testid="recipe-access-check"
          className="btn-solid-gold !py-2 !px-5 text-[10px] whitespace-nowrap disabled:opacity-50"
        >
          {loading ? "..." : "Ξεκλείδωμα"}
        </button>
      </div>
    </div>

    {/* Pricing tiers */}
    <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
      <PricingCard
        title="Εφάπαξ"
        price={`€${preview.price?.toFixed?.(0) ?? "50"}`}
        sub="Lifetime πρόσβαση"
        features={[
          `${preview.total_recipes}+ συνταγές`,
          "Πρόσβαση εφ' όρου ζωής",
          "Όλες οι μελλοντικές ενημερώσεις",
          "Print-friendly format",
        ]}
        cta="Απόκτηση"
        onClick={() => onRequest("one_time")}
        testid="recipe-plan-onetime"
      />
      <PricingCard
        title="Συνδρομή"
        price={`€${preview.subscription_price?.toFixed?.(0) ?? "10"}`}
        sub="/μήνα · ανανέωση κάθε 7 ημέρες"
        features={[
          "6 συνταγές την εβδομάδα",
          "Νέες συνταγές κάθε Δευτέρα",
          "Σεζονικά menu",
          "Ακύρωση όποτε θες",
        ]}
        cta="Εγγραφή"
        onClick={() => onRequest("subscription")}
        testid="recipe-plan-subscription"
      />
      <PricingCard
        title="ΔΩΡΕΑΝ"
        price="≥ €100"
        sub="Με κάθε παραγγελία"
        features={[
          "Πλήρης πρόσβαση",
          "Lifetime",
          "Αυτόματη αποστολή στο email σου",
        ]}
        cta="Δες προϊόντα"
        highlight
        onClick={() => { document.getElementById("heritage")?.scrollIntoView({ behavior: "smooth" }); }}
        testid="recipe-plan-gift"
      />
    </div>
  </div>
);

/* ============ UNLOCKED VIEW ============ */
const UnlockedView = ({ data, onLogout, onPickRecipe }) => {
  const { recipes, source, rotation } = data;
  const sourceLabel =
    source === "admin" ? "Admin (πλήρης πρόσβαση)"
      : source === "order" ? "Παραγγελία ≥ €100 🎁"
      : source === "subscription" ? "Εβδομαδιαία συνδρομή"
      : source === "payment" ? "Lifetime €50"
      : "Manual grant";
  return (
    <div className="p-7 sm:p-12">
      <div className="text-center mb-10">
        <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
          <BookOpen size={12} className="inline -translate-y-0.5 mr-1" /> Το Βιβλίο των Συνταγών
        </p>
        <h2 className="font-serif-luxury text-3xl sm:text-4xl gold-gradient-text mb-3 leading-tight">
          {recipes.length} συνταγές διαθέσιμες
        </h2>
        <p className="font-serif-luxury italic text-[color:var(--goldenelia-cream)]/75 mb-1">
          Πρόσβαση: <span className="text-[color:var(--goldenelia-gold)]">{sourceLabel}</span>
        </p>
        {rotation?.is_rotating && (
          <p className="font-body text-xs text-[color:var(--goldenelia-gold)]/75 mt-1">
            🔄 Συνδρομή: 6 συνταγές που ανανεώνονται κάθε 7 ημέρες — βιβλιοθήκη {rotation.library_size}+ συνταγών
          </p>
        )}
        <button onClick={onLogout} data-testid="recipe-logout" className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/45 hover:text-[color:var(--goldenelia-gold)] underline mt-3">
          Αλλαγή email
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {recipes.map((r) => (
          <button
            key={r.slug}
            data-testid={`recipe-${r.slug}`}
            onClick={() => onPickRecipe(r)}
            className="text-left p-5 border border-[color:var(--goldenelia-gold)]/25 bg-black/60 hover:border-[color:var(--goldenelia-gold)]/60 hover:bg-black/80 transition-colors"
          >
            <p className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
              {CATEGORY_LABELS[r.category] || r.category}
            </p>
            <h3 className="font-serif-luxury text-lg text-[color:var(--goldenelia-cream)] mb-2 leading-tight">{r.title}</h3>
            <div className="flex gap-3 font-body text-[10px] text-[color:var(--goldenelia-cream)]/55 flex-wrap">
              <span className="inline-flex items-center gap-1"><Clock size={10} /> {r.time}</span>
              <span className="inline-flex items-center gap-1"><Users size={10} /> {r.serves}</span>
              <span>{SEASON_LABELS[r.season] || ""}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ============ subcomponents ============ */
const PricingCard = ({ title, price, sub, features, cta, onClick, highlight, testid }) => (
  <div className={`p-5 border ${highlight ? "border-[color:var(--goldenelia-gold)] bg-[color:var(--goldenelia-gold)]/8" : "border-[color:var(--goldenelia-gold)]/25 bg-black/40"} text-center flex flex-col`}>
    {highlight && (
      <p className="font-display text-[8px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold-bright)] mb-2 inline-flex items-center justify-center gap-1">
        <Sparkles size={10} /> Δώρο
      </p>
    )}
    <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-2">{title}</p>
    <p className="font-serif-luxury text-3xl gold-gradient-text mb-1">{price}</p>
    <p className="font-body text-[11px] text-[color:var(--goldenelia-cream)]/55 mb-4">{sub}</p>
    <ul className="space-y-1.5 font-body text-[13px] text-[color:var(--goldenelia-cream)]/80 mb-5 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 justify-center text-left"><Check size={12} className="text-[color:var(--goldenelia-gold)] mt-1 shrink-0" /><span>{f}</span></li>
      ))}
    </ul>
    <button onClick={onClick} data-testid={testid} className={`${highlight ? "btn-solid-gold" : "btn-ghost-gold"} !py-2 !px-5 text-[10px] w-full`}>
      {cta}
    </button>
  </div>
);

const RequestModal = ({ form, setForm, onSubmit, onClose, price, subPrice }) => (
  <div data-testid="recipe-request-modal" className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/90 backdrop-blur" onClick={onClose}>
    <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="max-w-md w-full bg-black border border-[color:var(--goldenelia-gold)]/40 p-7 space-y-4">
      <h3 className="font-serif-luxury text-2xl gold-gradient-text">Αίτημα Πρόσβασης</h3>
      <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/60">
        Συμπλήρωσε τα στοιχεία σου. Θα επικοινωνήσουμε εντός 24h για τις λεπτομέρειες πληρωμής (τραπεζική κατάθεση ή PayPal).
      </p>
      <input type="text" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} placeholder="Ονοματεπώνυμο" required data-testid="recipe-req-name" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/40 px-4 py-2.5 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" required data-testid="recipe-req-email" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/40 px-4 py-2.5 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Τηλέφωνο/WhatsApp (προαιρετικό)" data-testid="recipe-req-phone" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/40 px-4 py-2.5 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <p className="font-display text-[10px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]">
        Πλάνο: {form.plan === "one_time" ? `Εφάπαξ €${price ?? 50}` : `Συνδρομή €${subPrice ?? 10}/μήνα`}
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={onClose} className="btn-ghost-gold !py-2 !px-4 text-[10px] flex-1">Άκυρο</button>
        <button type="submit" data-testid="recipe-req-submit" className="btn-solid-gold !py-2 !px-4 text-[10px] flex-1">Αποστολή</button>
      </div>
    </form>
  </div>
);

const RecipeModal = ({ recipe, onClose }) => (
  <div data-testid="recipe-detail-modal" className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/95 backdrop-blur" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="max-w-2xl w-full bg-black border border-[color:var(--goldenelia-gold)]/40 p-7 max-h-[88vh] overflow-auto">
      <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
        {CATEGORY_LABELS[recipe.category]} · {recipe.time} · {recipe.serves} άτομα
      </p>
      <h3 className="font-serif-luxury text-3xl gold-gradient-text mb-3">{recipe.title}</h3>

      {recipe.origin && (
        <div className="mb-5 p-3 border-l-2 border-[color:var(--goldenelia-gold)]/60 bg-[color:var(--goldenelia-gold)]/5">
          <p className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)] mb-1 inline-flex items-center gap-1">
            <MountainSnow size={10} /> Καταγωγή
          </p>
          <p className="font-serif-luxury italic text-[13px] text-[color:var(--goldenelia-cream)]/85 leading-relaxed">{recipe.origin}</p>
        </div>
      )}

      <p className="font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold-bright)] mt-4 mb-2">Υλικά</p>
      <ul className="space-y-1.5 font-body text-[14px] text-[color:var(--goldenelia-cream)]/85">
        {recipe.ingredients.map((it, i) => (
          <li key={i} className="flex gap-2"><span className="text-[color:var(--goldenelia-gold)]">·</span>{it}</li>
        ))}
      </ul>

      <p className="font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold-bright)] mt-6 mb-2">Εκτέλεση</p>
      <ol className="space-y-2 font-body text-[14px] text-[color:var(--goldenelia-cream)]/85">
        {recipe.steps.map((s, i) => (
          <li key={i} className="flex gap-3"><span className="font-display text-[color:var(--goldenelia-gold)] shrink-0">{i + 1}.</span>{s}</li>
        ))}
      </ol>

      {recipe.tip && (
        <div className="mt-6 p-4 border-l-2 border-[color:var(--goldenelia-gold)] bg-[color:var(--goldenelia-gold)]/5">
          <p className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
            <Sparkles size={10} className="inline -translate-y-0.5 mr-1" /> Tip του Σεφ
          </p>
          <p className="font-serif-luxury italic text-[14px] text-[color:var(--goldenelia-cream)]/85">{recipe.tip}</p>
        </div>
      )}

      <button onClick={onClose} className="mt-7 btn-ghost-gold !py-2 !px-5 text-[10px]">Κλείσιμο</button>
    </div>
  </div>
);

export default RecipeBook;
